import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Typography, Empty, Spin, Skeleton, Input, Tag, Progress, Tooltip } from 'antd';
import {
  EnvironmentOutlined,
  SearchOutlined,
  TrophyOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_map from 'highcharts/modules/map';
import HC_exporting from 'highcharts/modules/exporting';
import HC_accessibility from 'highcharts/modules/accessibility';
import { glassCardStyle } from '../../Performance/utils';
import { ProvinsiCount } from '../../../types/dashboard';

HC_map(Highcharts);
HC_exporting(Highcharts);
HC_accessibility(Highcharts);

const { Title, Text } = Typography;

interface LeadsProvinceMapCardProps {
  statsData: any;
  navigate: any;
  message: any;
  mapError: boolean;
  setMapError: (err: boolean) => void;
  loading: boolean;
}

const provinsiHighchartsMapping: { [key: string]: string } = {
  'Aceh': 'id-ac',
  'Sumatera Utara': 'id-su',
  'Sumatera Barat': 'id-sb',
  'Riau': 'id-ri',
  'Jambi': 'id-ja',
  'Sumatera Selatan': 'id-sl',
  'Bengkulu': 'id-be',
  'Lampung': 'id-1024',
  'Kepulauan Bangka Belitung': 'id-bb',
  'Kepulauan Riau': 'id-kr',
  'DKI Jakarta': 'id-jk',
  'Jawa Barat': 'id-jr',
  'Jawa Tengah': 'id-jt',
  'DI Yogyakarta': 'id-yo',
  'Jawa Timur': 'id-ji',
  'Banten': 'id-bt',
  'Bali': 'id-ba',
  'Nusa Tenggara Barat': 'id-nb',
  'Nusa Tenggara Timur': 'id-nt',
  'Kalimantan Barat': 'id-kb',
  'Kalimantan Tengah': 'id-kt',
  'Kalimantan Selatan': 'id-ks',
  'Kalimantan Timur': 'id-ki',
  'Kalimantan Utara': 'id-ku',
  'Sulawesi Utara': 'id-sw',
  'Sulawesi Tengah': 'id-st',
  'Sulawesi Selatan': 'id-sn',
  'Sulawesi Tenggara': 'id-sg',
  'Gorontalo': 'id-go',
  'Sulawesi Barat': 'id-sr',
  'Maluku': 'id-ma',
  'Maluku Utara': 'id-mu',
  'Papua Barat': 'id-ib',
  'Papua': 'id-pa',
};

const IndonesiaHighchartsMap = ({
  data,
  navigate,
  message,
}: {
  data: any;
  navigate: any;
  message: any;
}) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadIndonesiaMap = async () => {
      try {
        const response = await fetch('https://code.highcharts.com/mapdata/countries/id/id-all.geo.json');
        const geoJson = await response.json();
        setMapData(geoJson);
      } catch (error) {
        console.error('Failed to load Indonesia map:', error);
      } finally {
        setLoading(false);
      }
    };
    loadIndonesiaMap();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Spin tip="Memuat peta Indonesia...">
          <div
            style={{
              padding: '60px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              backdropFilter: 'blur(5px)',
            }}
          />
        </Spin>
      </div>
    );
  }

  if (!data?.leads_by_provinsi || data.leads_by_provinsi.length === 0) {
    return <Empty description="Data provinsi tidak tersedia" />;
  }

  const processedData = data.leads_by_provinsi.reduce((acc: any[], item: any) => {
    const cleanedProvinsi = item.provinsi ? item.provinsi.trim().replace(/\s+/g, ' ') : '';
    const hcKey = provinsiHighchartsMapping[cleanedProvinsi];

    if (hcKey) {
      acc.push({
        'hc-key': hcKey,
        name: cleanedProvinsi,
        value: item.count,
        provinsi_id: item.provinsi_id,
        cold_count: item.cold_count,
        hot_count: item.hot_count,
        rsv_count: item.rsv_count,
        closing_ok_count: item.closing_ok_count,
      });
    } else if (cleanedProvinsi) {
      console.warn(`[Dashboard Map] Province name "${cleanedProvinsi}" from API not found in frontend mapping and will be ignored.`);
    }
    return acc;
  }, []);

  const mapOptions = {
    chart: {
      map: mapData,
      height: 480,
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      },
    },
    title: {
      text: undefined,
    },
    subtitle: {
      text: undefined,
    },
    mapNavigation: { enabled: false },
    colorAxis: {
      min: 1,
      minColor: '#fef08a', // Amber/Yellow 100
      maxColor: '#7BAA18', // Emerald brand
      stops: [
        [0, '#fef08a'],
        [0.45, '#a3e635'],
        [1, '#7BAA18'],
      ],
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { color: '#475569', fontSize: '11px' },
    },
    series: [
      {
        data: processedData,
        mapData: mapData,
        joinBy: 'hc-key',
        name: 'Jumlah Leads',
        states: {
          hover: {
            color: '#d9f99d', // Lime 200
            borderColor: '#1e293b',
            borderWidth: 1.5,
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          useHTML: true,
          headerFormat: '',
          pointFormatter: function (this: any) {
            const p = this;
            let html = `
              <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 4px 2px; min-width: 140px;">
                <div style="font-weight: 700; font-size: 13px; color: #0f172a; margin-bottom: 4px;">${p.name}</div>
                <div style="font-size: 12px; color: #334155; margin-bottom: 6px;">
                  Total Leads: <b style="color: #7BAA18; font-size: 13px;">${(p.value || 0).toLocaleString()}</b>
                </div>`;

            const hasBreakdown =
              p.cold_count !== undefined ||
              p.hot_count !== undefined ||
              p.rsv_count !== undefined ||
              p.closing_ok_count !== undefined;

            if (hasBreakdown) {
              html += `<div style="border-top: 1px solid #e2e8f0; padding-top: 5px; display: flex; flex-direction: column; gap: 3px; font-size: 11px;">`;
              if (p.closing_ok_count !== undefined && p.closing_ok_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #10b981; font-weight: 600;">🏆 Closing OK:</span> <b>${p.closing_ok_count}</b></div>`;
              }
              if (p.rsv_count !== undefined && p.rsv_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #f59e0b; font-weight: 600;">📋 Reservasi:</span> <b>${p.rsv_count}</b></div>`;
              }
              if (p.hot_count !== undefined && p.hot_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #ef4444; font-weight: 600;">🔥 Hot:</span> <b>${p.hot_count}</b></div>`;
              }
              if (p.cold_count !== undefined && p.cold_count > 0) {
                html += `<div style="display: flex; justify-content: space-between;"><span style="color: #64748b; font-weight: 600;">❄️ Cold:</span> <b>${p.cold_count}</b></div>`;
              }
              html += `</div>`;
            }
            html += `</div>`;
            return html;
          },
        },
      },
    ],
    plotOptions: {
      map: {
        events: {
          click: function (e: any) {
            if (e.point) {
              message.info(`${e.point.name}: ${e.point.value} leads`);
              navigate(`/leads/data?provinsi_id=${e.point.provinsi_id}`);
            }
          },
        },
      },
    },
    credits: { enabled: false },
  };

  return <HighchartsReact highcharts={Highcharts} options={mapOptions} constructorType={'mapChart'} />;
};

const ProvinceHighchartsBar = ({ data }: { data: any }) => {
  if (!data?.leads_by_provinsi || data.leads_by_provinsi.length === 0) return <Empty description="Tidak ada data provinsi" />;
  const sortedData = [...data.leads_by_provinsi].sort((a, b) => b.count - a.count);

  const barOptions = {
    chart: {
      type: 'bar',
      height: 480,
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      },
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: sortedData.map((item) => item.provinsi),
      labels: { style: { color: '#64748b' } },
      title: { text: null },
    },
    yAxis: {
      min: 0,
      title: { text: 'Jumlah Leads', align: 'high', style: { color: '#64748b' } },
      labels: { overflow: 'justify', style: { color: '#64748b' } },
      gridLineColor: 'rgba(226, 232, 240, 0.6)',
    },
    tooltip: { valueSuffix: ' leads' },
    plotOptions: {
      bar: {
        dataLabels: { enabled: true, style: { textOutline: 'none' } },
        colorByPoint: true,
        borderRadius: 4,
      },
    },
    colors: ['#7BAA18', '#1890ff', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2'],
    legend: { enabled: false },
    credits: { enabled: false },
    series: [{ name: 'Leads', data: sortedData.map((item) => item.count) }],
  };

  return <HighchartsReact highcharts={Highcharts} options={barOptions} />;
};

// Panel Leaderboard Provinsi Modern (Sisi Kanan)
const ProvinceLeaderboardPanel: React.FC<{
  leadsByProvinsi: ProvinsiCount[];
  navigate: any;
  message: any;
}> = ({ leadsByProvinsi, navigate, message }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Total Leads dari seluruh provinsi
  const totalLeads = useMemo(() => {
    return leadsByProvinsi.reduce((acc, curr) => acc + (curr.count || 0), 0);
  }, [leadsByProvinsi]);

  // Urutkan provinsi dari jumlah lead terbanyak ke terendah
  const sortedList = useMemo(() => {
    return [...leadsByProvinsi].sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [leadsByProvinsi]);

  // Lead count terbesar untuk perhitungan lebar relative progress bar
  const maxCount = useMemo(() => {
    return sortedList[0]?.count || 1;
  }, [sortedList]);

  // Filter pencarian berdasarkan nama provinsi
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return sortedList;
    const term = searchTerm.toLowerCase().trim();
    return sortedList.filter((item) => item.provinsi?.toLowerCase().includes(term));
  }, [sortedList, searchTerm]);

  const handleProvinceClick = (item: ProvinsiCount) => {
    message.info(`Menyaring leads ${item.provinsi}: ${item.count} leads`);
    navigate(`/leads/data?provinsi_id=${item.provinsi_id}`);
  };

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.7)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '480px',
      }}
    >
      {/* Header Panel */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
              }}
            >
              <TrophyOutlined />
            </div>
            <Title level={5} style={{ margin: 0, color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>
              Peringkat Provinsi
            </Title>
          </div>
          <Tag
            color="success"
            style={{
              margin: 0,
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 600,
              padding: '1px 8px',
              border: 'none',
              background: 'rgba(123, 170, 24, 0.15)',
              color: '#55790f',
            }}
          >
            {sortedList.length} Provinsi
          </Tag>
        </div>
        <Text type="secondary" style={{ fontSize: '12px', color: '#64748b' }}>
          Total akumulasi: <b style={{ color: '#0f172a' }}>{totalLeads.toLocaleString()} leads</b>
        </Text>

        {/* Input Pencarian */}
        <div style={{ marginTop: '10px' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Cari provinsi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size="middle"
            style={{
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
            }}
          />
        </div>
      </div>

      {/* Scrollable List Container */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '380px',
          paddingRight: '4px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {filteredList.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Provinsi tidak ditemukan" />
          </div>
        ) : (
          filteredList.map((item) => {
            // Dapatkan ranking asli dari list yang diurutkan
            const rank = sortedList.findIndex((p) => p.provinsi_id === item.provinsi_id) + 1;
            const percentage = totalLeads > 0 ? ((item.count / totalLeads) * 100).toFixed(1) : '0';
            const progressRelative = Math.round(((item.count || 0) / maxCount) * 100);

            // Gaya badge ranking
            let rankBadgeStyle: React.CSSProperties = {
              minWidth: '24px',
              height: '24px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 800,
            };

            if (rank === 1) {
              rankBadgeStyle = {
                ...rankBadgeStyle,
                background: 'linear-gradient(135deg, #fef08a 0%, #facc15 100%)',
                color: '#854d0e',
                boxShadow: '0 2px 6px rgba(250, 204, 21, 0.35)',
              };
            } else if (rank === 2) {
              rankBadgeStyle = {
                ...rankBadgeStyle,
                background: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)',
                color: '#334155',
                boxShadow: '0 2px 6px rgba(148, 163, 184, 0.3)',
              };
            } else if (rank === 3) {
              rankBadgeStyle = {
                ...rankBadgeStyle,
                background: 'linear-gradient(135deg, #ffedd5 0%, #fdba74 100%)',
                color: '#9a3412',
                boxShadow: '0 2px 6px rgba(251, 146, 60, 0.3)',
              };
            } else {
              rankBadgeStyle = {
                ...rankBadgeStyle,
                background: 'rgba(241, 245, 249, 0.85)',
                color: '#64748b',
              };
            }

            const hasBreakdown =
              item.cold_count !== undefined ||
              item.hot_count !== undefined ||
              item.rsv_count !== undefined ||
              item.closing_ok_count !== undefined;

            return (
              <div
                key={item.provinsi_id || item.provinsi}
                onClick={() => handleProvinceClick(item)}
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                className="province-rank-item"
              >
                {/* Header baris: Badge Rank, Nama Provinsi, Count & Persentase */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                    <div style={rankBadgeStyle}>#{rank}</div>
                    <Text
                      ellipsis={{ tooltip: item.provinsi }}
                      style={{
                        fontWeight: 700,
                        fontSize: '13px',
                        color: '#1e293b',
                      }}
                    >
                      {item.provinsi}
                    </Text>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#7BAA18' }}>
                      {item.count.toLocaleString()}
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                      ({percentage}%)
                    </span>
                    <ArrowRightOutlined
                      style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        marginLeft: '2px',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </div>
                </div>

                {/* Relative Progress Bar */}
                <div style={{ marginTop: '4px' }}>
                  <Progress
                    percent={progressRelative}
                    showInfo={false}
                    size="small"
                    strokeColor={rank === 1 ? '#7BAA18' : rank === 2 ? '#84cc16' : '#a3e635'}
                    trailColor="rgba(0, 0, 0, 0.04)"
                    style={{ margin: 0 }}
                  />
                </div>

                {/* Sub-Badges Suhu / Kualitas Leads (Jika Disediakan BE) */}
                {hasBreakdown && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                    {item.closing_ok_count !== undefined && item.closing_ok_count > 0 && (
                      <Tooltip title="Transaksi Closing Disetujui">
                        <Tag
                          style={{
                            margin: 0,
                            fontSize: '10px',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'rgba(16, 185, 129, 0.12)',
                            color: '#059669',
                            fontWeight: 600,
                            padding: '0 5px',
                            lineHeight: '18px',
                          }}
                        >
                          🏆 {item.closing_ok_count} OK
                        </Tag>
                      </Tooltip>
                    )}
                    {item.rsv_count !== undefined && item.rsv_count > 0 && (
                      <Tooltip title="Reservasi Booking Unit">
                        <Tag
                          style={{
                            margin: 0,
                            fontSize: '10px',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'rgba(245, 158, 11, 0.12)',
                            color: '#d97706',
                            fontWeight: 600,
                            padding: '0 5px',
                            lineHeight: '18px',
                          }}
                        >
                          📋 {item.rsv_count} RSV
                        </Tag>
                      </Tooltip>
                    )}
                    {item.hot_count !== undefined && item.hot_count > 0 && (
                      <Tooltip title="Prospek Hot">
                        <Tag
                          style={{
                            margin: 0,
                            fontSize: '10px',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'rgba(239, 68, 68, 0.12)',
                            color: '#dc2626',
                            fontWeight: 600,
                            padding: '0 5px',
                            lineHeight: '18px',
                          }}
                        >
                          🔥 {item.hot_count} Hot
                        </Tag>
                      </Tooltip>
                    )}
                    {item.cold_count !== undefined && item.cold_count > 0 && (
                      <Tooltip title="Prospek Cold">
                        <Tag
                          style={{
                            margin: 0,
                            fontSize: '10px',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'rgba(100, 116, 139, 0.12)',
                            color: '#475569',
                            fontWeight: 600,
                            padding: '0 5px',
                            lineHeight: '18px',
                          }}
                        >
                          ❄️ {item.cold_count} Cold
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer Info / Petunjuk Interaksi */}
      <div
        style={{
          marginTop: '10px',
          paddingTop: '8px',
          borderTop: '1px solid rgba(226, 232, 240, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: '#94a3b8',
        }}
      >
        <span>
          <EnvironmentOutlined style={{ marginRight: '4px' }} />
          Klik provinsi untuk filter leads
        </span>
        <span style={{ fontWeight: 600, color: '#7BAA18' }}>Urut Berdasarkan Leads ↓</span>
      </div>
    </div>
  );
};

export const LeadsProvinceMapCard: React.FC<LeadsProvinceMapCardProps> = ({
  statsData,
  navigate,
  message,
  mapError,
  setMapError,
  loading,
}) => {
  const isCardLoading = loading || !statsData;
  const leadsByProvinsi: ProvinsiCount[] = statsData?.leads_by_provinsi || [];

  return (
    <div style={{ ...glassCardStyle, marginBottom: '24px' }} className="glass-card-interactive">
      {/* Header Kartu */}
      <div style={{ marginBottom: '20px' }}>
        <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
          Peta Sebaran Geografis &amp; Peringkat Wilayah (Nasional)
        </Title>
        <Text type="secondary" style={{ fontSize: '13px' }}>
          Pantau konsentrasi asal provinsi para calon pembeli di seluruh Indonesia serta peringkat kontribusi daerah secara interaktif.
        </Text>
      </div>

      {isCardLoading ? (
        <div style={{ padding: '40px 0' }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : leadsByProvinsi.length > 0 ? (
        /* Split View Layout: Peta di Kiri (Col 15) & Leaderboard Panel di Kanan (Col 9) */
        <Row gutter={[20, 20]} align="stretch">
          <Col xs={24} lg={15} xl={15}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.35)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                padding: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {!mapError ? (
                <div onError={() => setMapError(true)}>
                  <IndonesiaHighchartsMap data={statsData} navigate={navigate} message={message} />
                </div>
              ) : (
                <ProvinceHighchartsBar data={statsData} />
              )}
            </div>
          </Col>

          <Col xs={24} lg={9} xl={9}>
            <ProvinceLeaderboardPanel
              leadsByProvinsi={leadsByProvinsi}
              navigate={navigate}
              message={message}
            />
          </Col>
        </Row>
      ) : (
        <Empty description="Tidak ada data provinsi" />
      )}
    </div>
  );
};

export default LeadsProvinceMapCard;
